import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { app as TeamsApp } from '@microsoft/teams-js'
import { onTeamsThemeChange, type HostKind, type TeamsThemeName } from '@/lib/teamsHost'

/** What actually gets painted — the value written to <html data-theme>. */
export type AppliedTheme = 'light' | 'dark' | 'contrast'

/** What the user explicitly chose. `null` = follow the host (Teams / default). */
export type UserThemePreference = 'light' | 'dark' | null

export const THEME_STORAGE_KEY = 'wacner-sales-theme'

interface HostContextValue {
  host: HostKind
  theme: TeamsThemeName
  teamsContext: TeamsApp.Context | null
  /** The theme currently applied to the document. */
  appliedTheme: AppliedTheme
  /** The user's explicit choice, or null when following the host. */
  userTheme: UserThemePreference
  setUserTheme: (theme: UserThemePreference) => void
  /** Flips between light and dark, pinning the result as an explicit choice. */
  toggleTheme: () => void
}

const HostContext = createContext<HostContextValue>({
  host: 'web',
  theme: 'default',
  teamsContext: null,
  appliedTheme: 'light',
  userTheme: null,
  setUserTheme: () => {},
  toggleTheme: () => {},
})

function readStoredTheme(): UserThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY)
    return raw === 'light' || raw === 'dark' ? raw : null
  } catch {
    // Storage can throw (private mode, blocked cookies). Falling back to the
    // host theme is correct — the app must still render.
    return null
  }
}

function writeStoredTheme(theme: UserThemePreference): void {
  try {
    if (theme === null) localStorage.removeItem(THEME_STORAGE_KEY)
    else localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Non-fatal: the choice just won't survive a reload.
  }
}

/** Maps the host-reported (Teams) theme onto what we can actually paint. */
function hostThemeToApplied(theme: TeamsThemeName): AppliedTheme {
  if (theme === 'dark') return 'dark'
  if (theme === 'contrast') return 'contrast'
  // 'glass' has no token set of its own; it reads as the light surface.
  return 'light'
}

export function resolveAppliedTheme(
  host: HostKind,
  theme: TeamsThemeName,
  userTheme: UserThemePreference,
): AppliedTheme {
  return host === 'teams' ? hostThemeToApplied(theme) : userTheme ?? hostThemeToApplied(theme)
}

/**
 * Provides the host (web vs Teams tab) and the live theme.
 *
 * Theme precedence depends on the host:
 *   1. inside Teams, the live Teams theme is authoritative
 *   2. on the web, `userTheme` from localStorage wins when present
 *   3. otherwise the host/default theme is used
 *
 * Rationale: in a normal browser Teams reports nothing, so without (1) there is
 * no way to reach dark mode at all. Inside Teams, a stored web preference is
 * deliberately ignored so Sales always follows the Teams light/dark/contrast
 * setting. Returning to the web exposes the stored manual preference again.
 *
 * The applied value is mirrored to <html data-theme>, which is what
 * design-tokens.css keys off. index.html applies the stored preference before
 * this module ever runs, so there is no flash of the wrong theme on load.
 */
export const HostProvider: React.FC<{
  host: HostKind
  initialTheme: TeamsThemeName
  teamsContext: TeamsApp.Context | null
  children: React.ReactNode
}> = ({ host, initialTheme, teamsContext, children }) => {
  const [theme, setTheme] = useState<TeamsThemeName>(initialTheme)
  const [userTheme, setUserThemeState] = useState<UserThemePreference>(() => readStoredTheme())

  useEffect(() => {
    if (host !== 'teams') return
    onTeamsThemeChange(setTheme)
  }, [host])

  const appliedTheme = resolveAppliedTheme(host, theme, userTheme)

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', appliedTheme)
  }, [appliedTheme])

  const setUserTheme = useCallback((next: UserThemePreference) => {
    writeStoredTheme(next)
    setUserThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setUserTheme(appliedTheme === 'dark' ? 'light' : 'dark')
  }, [appliedTheme, setUserTheme])

  const value = useMemo(
    () => ({ host, theme, teamsContext, appliedTheme, userTheme, setUserTheme, toggleTheme }),
    [host, theme, teamsContext, appliedTheme, userTheme, setUserTheme, toggleTheme]
  )

  return <HostContext.Provider value={value}>{children}</HostContext.Provider>
}

export function useHostContext(): HostContextValue {
  return useContext(HostContext)
}
